import {
  ChakraFlex,
  ChakraSpacer,
  ChakraText,
  EquipUITheme,
  UniversalSelect,
} from '@equip.health/ui';

import SubTab from '~/components/common/SubTab';
import { MESSAGES_TITLE } from '~/lib/constants/chat.constants';
import { ChatMessage, ChatUser } from '~/lib/types/chat';
import { ChatChannelTab } from '~/lib/util/chat/chatChannel.util';
import { getSelectedUniversalSelectOption } from '~/lib/util/inquiry.util';

import ChannelAttachments from './ChannelAttachments';
import ChannelMembers from '../../common/ChannelMembers';
import { PATIENT_MESSAGES_SIDEBAR_WIDTH } from '~/lib/constants/patients.constants';

const idPrefix = 'messages';
const idPostfix = 'header';

const { h5 } = EquipUITheme.typography;

const tabIndexToTabListMapper: Record<ChatChannelTab, string> = {
  [ChatChannelTab.EXTERNAL]: 'External',
  [ChatChannelTab.INTERNAL]: 'Internal',
};

interface MessagesHeaderProps {
  channelAttachments?: ChatMessage[];
  channelMembers: ChatUser[];
  channelTabIndex: number;
  channelTitle: string;
  id: string;
  isChannelReadOnly: boolean;
  onChannelTabChange: (tab: number) => void;
  onMessageRemoved: (messageId: number, replyId?: number) => void;
  unreadCount: Record<ChatChannelTab, number>;
  selectedChannelType: string;
  channelTypeOptions: UniversalSelectOption[];
  onChannelTypeSelect: (newChannelType: string) => void;
}

const MessagesHeader = ({
  channelAttachments,
  channelMembers,
  channelTabIndex,
  channelTitle,
  id,
  isChannelReadOnly,
  onChannelTabChange,
  onMessageRemoved,
  unreadCount,
  onChannelTypeSelect,
  channelTypeOptions,
  selectedChannelType,
}: MessagesHeaderProps) => {
  return (
    <>
      <ChakraFlex
        flex="0 0 auto"
        gridGap="56px"
        id={`${idPrefix}-container__${id}__${idPostfix}`}
        width="100%"
      >
        <ChakraFlex
          alignItems="center"
          flex="0 0 auto"
          gridGap="10px"
          height="71px"
          id={`${idPrefix}-left-container__${id}__${idPostfix}`}
          justifyContent="space-between"
          width={PATIENT_MESSAGES_SIDEBAR_WIDTH}
        >
          <ChakraText
            id={`${idPrefix}-messages-title__${id}__${idPostfix}`}
            {...h5}
            color="neutral.primary"
          >
            {MESSAGES_TITLE}
          </ChakraText>
          <ChakraFlex align="center" id="choose-channel-type-container">
            <UniversalSelect
              id={`${idPrefix}-messages-title__${id}__${idPostfix}`}
              isClearable={false}
              isSingleSelect
              onChange={(selectedOption: UniversalSelectOption) =>
                onChannelTypeSelect(selectedOption.value)
              }
              options={channelTypeOptions}
              value={getSelectedUniversalSelectOption(
                selectedChannelType,
                channelTypeOptions,
              )}
              variant="slim"
            />
          </ChakraFlex>
        </ChakraFlex>
        <ChakraFlex
          alignItems="center"
          borderBottomColor="neutral.line"
          borderBottomWidth="1px"
          flex={1}
          gridGap="10px"
          height="71px"
          id={`${idPrefix}-right-container__${id}__${idPostfix}`}
          justifyContent="space-between"
        >
          <ChannelMembers id={id} members={channelMembers} showPopoverOnHover />
          <ChakraText
            {...h5}
            id={`${idPrefix}-channel-title__${id}__${idPostfix}`}
          >
            {channelTitle}
          </ChakraText>
          <ChannelAttachments
            attachments={channelAttachments ?? []}
            id={id}
            isChannelReadOnly={isChannelReadOnly}
            onMessageRemoved={onMessageRemoved}
          />
        </ChakraFlex>
      </ChakraFlex>
      <ChakraFlex>
        <ChakraFlex
          align="center"
          background="neutral.background.secondary"
          borderRadius="48px"
          padding="4px"
        >
          <SubTab
            backgroundColor="primary.100"
            badgeValue={unreadCount[ChatChannelTab.INTERNAL]}
            color="white"
            isSelected={Number(ChatChannelTab.INTERNAL) === channelTabIndex}
            label={tabIndexToTabListMapper[ChatChannelTab.INTERNAL]}
            onChange={onChannelTabChange}
            value={ChatChannelTab.INTERNAL}
          />
          <ChakraSpacer width="8px" />
          <SubTab
            backgroundColor="primary.100"
            badgeValue={unreadCount[ChatChannelTab.EXTERNAL]}
            color="white"
            isSelected={Number(ChatChannelTab.EXTERNAL) === channelTabIndex}
            label={tabIndexToTabListMapper[ChatChannelTab.EXTERNAL]}
            onChange={onChannelTabChange}
            value={ChatChannelTab.EXTERNAL}
          />
        </ChakraFlex>
      </ChakraFlex>
    </>
  );
};

export default MessagesHeader;
